// ─────────────────────────────────────────────
//  useAicByFacility — each campus's daily-board leadership + working doctors,
//  read live from daily_boards (readable cross-facility for doctors/AIC/
//  admin/board-manager). Drives the MD Call location colour-coding:
//    • aicByFacility      { fid: aicName }   — WC AIC / Tampa DOD / CAR AIC
//    • doctorsByFacility  { fid: [names] }   — every rostered supervisor,
//      so EVERY doctor's call badge can be tinted by their campus.
//  Pass `enabled:false` for roles without the cross-read (colours just
//  don't render). Missing boards resolve to empty values quietly.
// ─────────────────────────────────────────────
import { useEffect, useState } from "react";
import { doc, onSnapshot } from "../firebase/sdk";
import { db } from "../firebase/config";
import { useFacilities } from "./useFacilities";
import { activeFacilityIds } from "../domain/facilities";

const EMPTY = { aicByFacility: {}, doctorsByFacility: {} };

export function useAicByFacility(dateKey, { enabled = true } = {}) {
  const [state, setState] = useState(EMPTY);
  // Cross-campus colouring must cover a newly-added facility (blocker #3), so
  // the campus list is derived LIVE from the facilities collection instead of a
  // hardcoded 3-id array; activeFacilityIds falls back to the cores if empty.
  const facilities = useFacilities();
  const fids = activeFacilityIds(facilities);
  const fidsKey = fids.join(",");
  useEffect(() => {
    if (!enabled || !dateKey) {
      setState(EMPTY);
      return undefined;
    }
    const unsubs = fids.map((fid) =>
      onSnapshot(
        doc(db, "daily_boards", `${fid}_${dateKey}`),
        (snap) => {
          const d = snap.exists() ? snap.data() : {};
          const aic = d.aicName || "";
          const docs = (Array.isArray(d.supervisors) ? d.supervisors : [])
            .map((m) => m?.name)
            .filter(Boolean);
          setState((prev) => ({
            aicByFacility: { ...prev.aicByFacility, [fid]: aic },
            doctorsByFacility: { ...prev.doctorsByFacility, [fid]: docs },
          }));
        },
        () => { /* permission-denied for this role — colours just don't render */ }
      )
    );
    return () => unsubs.forEach((u) => u());
    // fidsKey re-subscribes when the active facility set changes (new campus).
  }, [dateKey, enabled, fidsKey]);
  return state;
}
